import { useQuery } from "@tanstack/react-query";
import { ApiError, insuranceOptions } from "@fm/api";
import type { ApiClient } from "@fm/api";
import type { InsuranceOption } from "@fm/api";
import { getApiBaseUrl } from "../lib/config";

export interface UseInsuranceOptionsOptions {
  apiKey: string;
  fleetId: string;
  vehicleId: string;
  apiBaseUrl?: string;
  enabled?: boolean;
}

/**
 * Hook to fetch the insurance options offered on one unit, at that unit's
 * price. A key without the read permission (403) falls back to the fleet's
 * list, which is what renters saw before the per-unit route existed.
 */
export function useInsuranceOptions(options: UseInsuranceOptionsOptions) {
  const { apiKey, fleetId, vehicleId, apiBaseUrl, enabled = true } = options;

  const apiClient: ApiClient = {
    baseUrl: getApiBaseUrl(apiBaseUrl),
    apiKey,
  };

  return useQuery<InsuranceOption[], Error>({
    queryKey: ["insurance-options", fleetId, "vehicle", vehicleId],
    queryFn: async () => {
      try {
        const response = await insuranceOptions.listForVehicle(
          apiClient,
          vehicleId,
        );
        return response.data;
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 403) throw error;

        const response = await insuranceOptions.list(apiClient, fleetId);
        return response.data;
      }
    },
    enabled: enabled && !!fleetId && !!vehicleId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
